/**
 * ApiRateLimitingPage — per-API-key rate limit console.
 *
 * Three panels:
 *   1. Plan-rate matrix — shows rpm / rph / rpd caps (effective + stored)
 *   2. Current-usage bars — live window counters vs caps
 *   3. Burst-override form — save custom rpm / rph / rpd, or clear to defaults
 *
 * GREEN accent only. No emerald/blue/orange/purple/dark/emoji.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useClearOverrides,
	useRateLimits,
	useRateUsage,
	useSaveOverrides,
} from "./hooks";
import type { WindowUsageRow } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL_BADGE_DEFAULT =
	"inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-600";
const LABEL_BADGE_OVERRIDE =
	"inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-green-800";

const WINDOW_LABELS: Record<"m" | "h" | "d", string> = {
	m: "Per minute",
	h: "Per hour",
	d: "Per day",
};

function windowKey(win: "m" | "h" | "d"): "rpm" | "rph" | "rpd" {
	if (win === "m") return "rpm";
	if (win === "h") return "rph";
	return "rpd";
}

function parseOverride(raw: string): number | null | "invalid" {
	const t = raw.trim();
	if (t === "") return null;
	const n = Number(t);
	if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 10_000_000) {
		return "invalid";
	}
	return n;
}

export interface UsageAggregate {
	total_count: number;
	total_limit: number;
	worst_pct: number;
	at_cap: boolean;
}

export function aggregateUsage(windows: WindowUsageRow[]): UsageAggregate {
	let total_count = 0;
	let total_limit = 0;
	let worst_pct = 0;
	let at_cap = false;
	for (const w of windows) {
		total_count += w.count;
		total_limit += w.limit;
		const pct = w.limit > 0 ? w.count / w.limit : 0;
		if (pct > worst_pct) worst_pct = pct;
		if (w.count >= w.limit) at_cap = true;
	}
	return {
		total_count,
		total_limit,
		worst_pct: Math.min(1, worst_pct),
		at_cap,
	};
}

function barToneFor(pct: number, atCap: boolean): string {
	if (atCap || pct >= 1) return "bg-red-600";
	if (pct >= 0.8) return "bg-yellow-500";
	return "bg-green-600";
}

export function ApiRateLimitingPage() {
	const [apiKeyId, setApiKeyId] = useState("");
	const [submittedKey, setSubmittedKey] = useState("");
	const [rpmInput, setRpmInput] = useState("");
	const [rphInput, setRphInput] = useState("");
	const [rpdInput, setRpdInput] = useState("");
	const [formError, setFormError] = useState<string | null>(null);

	const limitsQuery = useRateLimits(submittedKey);
	const usageQuery = useRateUsage(submittedKey);
	const saveMut = useSaveOverrides();
	const clearMut = useClearOverrides();

	const limits = limitsQuery.data;
	const usage = usageQuery.data;
	const usageAgg = useMemo(
		() => (usage ? aggregateUsage(usage.windows) : null),
		[usage],
	);

	const handleLookup = (e: FormEvent) => {
		e.preventDefault();
		const t = apiKeyId.trim();
		if (!t) return;
		setSubmittedKey(t);
		setRpmInput("");
		setRphInput("");
		setRpdInput("");
		setFormError(null);
	};

	const handleSave = (e: FormEvent) => {
		e.preventDefault();
		setFormError(null);
		if (!submittedKey) return;

		const rpmP = parseOverride(rpmInput);
		const rphP = parseOverride(rphInput);
		const rpdP = parseOverride(rpdInput);
		for (const [name, parsed] of [
			["rpm", rpmP],
			["rph", rphP],
			["rpd", rpdP],
		] as const) {
			if (parsed === "invalid") {
				setFormError(
					`${name} must be an integer between 1 and 10,000,000`,
				);
				return;
			}
		}

		saveMut.mutate(
			{
				api_key_id: submittedKey,
				rpm: rpmP as number | null,
				rph: rphP as number | null,
				rpd: rpdP as number | null,
			},
			{
				onSuccess: () => {
					setRpmInput("");
					setRphInput("");
					setRpdInput("");
				},
			},
		);
	};

	const handleClear = () => {
		if (!submittedKey) return;
		clearMut.mutate(submittedKey);
	};

	return (
		<main
			data-testid="api-rate-limiting-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					api-rate-limiting
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					API rate limiting
				</h1>
				<p className="text-sm text-zinc-600">
					Inspect per-key throughput caps, watch live counters, and
					dial in burst overrides.
				</p>
			</header>

			<section className={CARD} aria-label="API key lookup">
				<h2 className="text-base font-semibold text-zinc-900">
					Select API key
				</h2>
				<form
					onSubmit={handleLookup}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="api-rate-limiting-lookup-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="api_key_id"
						value={apiKeyId}
						onChange={(e) => setApiKeyId(e.currentTarget.value)}
						data-testid="api-rate-limiting-key-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={!apiKeyId.trim()}
						data-testid="api-rate-limiting-lookup-submit"
					>
						Load
					</button>
				</form>
				{submittedKey ? (
					<p className="mt-2 text-xs text-zinc-500">
						Showing{" "}
						<span
							className="font-mono text-zinc-800"
							data-testid="api-rate-limiting-selected-key"
						>
							{submittedKey}
						</span>
					</p>
				) : (
					<p
						className="mt-2 text-xs text-zinc-500"
						data-testid="api-rate-limiting-empty"
					>
						Enter an API key to load its plan matrix and usage.
					</p>
				)}
			</section>

			{submittedKey ? (
				<>
					<section
						className={CARD}
						aria-label="Plan rate matrix"
						data-testid="api-rate-limiting-matrix"
					>
						<div className="flex items-center justify-between">
							<h2 className="text-base font-semibold text-zinc-900">
								Plan rate matrix
							</h2>
							{limits?.has_override ? (
								<span className={LABEL_BADGE_OVERRIDE}>
									Custom override
								</span>
							) : (
								<span className={LABEL_BADGE_DEFAULT}>
									Platform default
								</span>
							)}
						</div>
						{limitsQuery.isLoading ? (
							<p
								className="mt-3 text-sm text-zinc-500"
								data-testid="api-rate-limiting-loading"
							>
								Loading.
							</p>
						) : limitsQuery.error ? (
							<p
								className="mt-3 text-sm text-red-600"
								role="alert"
							>
								{limitsQuery.error.message}
							</p>
						) : limits ? (
							<table className="mt-4 w-full border-collapse text-sm">
								<thead>
									<tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
										<th className="py-2 pr-4 font-medium">
											Window
										</th>
										<th className="py-2 pr-4 font-medium">
											Effective cap
										</th>
										<th className="py-2 pr-4 font-medium">
											Stored
										</th>
										<th className="py-2 font-medium">
											Source
										</th>
									</tr>
								</thead>
								<tbody>
									{(["m", "h", "d"] as const).map((win) => {
										const key = windowKey(win);
										const eff = limits.limits[key];
										const stored = limits.stored[key];
										const isDefault =
											limits.limits[
												`${key}_default` as const
											];
										return (
											<tr
												key={win}
												className="border-b border-zinc-100"
												data-testid={`api-rate-limiting-row-${win}`}
											>
												<td className="py-2 pr-4 text-zinc-700">
													{WINDOW_LABELS[win]}
												</td>
												<td className="py-2 pr-4 font-mono text-zinc-900">
													{eff.toLocaleString()}
												</td>
												<td className="py-2 pr-4 font-mono text-zinc-500">
													{stored == null
														? "—"
														: stored.toLocaleString()}
												</td>
												<td className="py-2">
													{isDefault ? (
														<span
															className={
																LABEL_BADGE_DEFAULT
															}
														>
															default
														</span>
													) : (
														<span
															className={
																LABEL_BADGE_OVERRIDE
															}
														>
															override
														</span>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						) : null}
					</section>

					<section
						className={CARD}
						aria-label="Current usage"
						data-testid="api-rate-limiting-usage"
					>
						<div className="flex items-center justify-between">
							<h2 className="text-base font-semibold text-zinc-900">
								Current usage
							</h2>
							{usageAgg ? (
								<span
									className="text-xs text-zinc-500"
									data-testid="api-rate-limiting-usage-worst"
								>
									Peak load{" "}
									<span className="font-semibold text-zinc-800">
										{Math.round(usageAgg.worst_pct * 100)}%
									</span>
								</span>
							) : null}
						</div>
						{usageQuery.isLoading ? (
							<p
								className="mt-3 text-sm text-zinc-500"
								data-testid="api-rate-limiting-usage-loading"
							>
								Loading.
							</p>
						) : usageQuery.error ? (
							<p
								className="mt-3 text-sm text-red-600"
								role="alert"
							>
								{usageQuery.error.message}
							</p>
						) : usage ? (
							<ul
								className="mt-4 flex flex-col gap-4"
								data-testid="api-rate-limiting-usage-list"
							>
								{usage.windows.map((w) => {
									const pct =
										w.limit > 0
											? Math.min(1, w.count / w.limit)
											: 0;
									const atCap = w.count >= w.limit;
									return (
										<li
											key={w.window}
											className="flex flex-col gap-1"
											data-testid={`api-rate-limiting-usage-${w.window}`}
										>
											<div className="flex items-center justify-between text-xs text-zinc-600">
												<span>
													{WINDOW_LABELS[w.window]}
												</span>
												<span className="font-mono">
													{w.count.toLocaleString()}
													{" / "}
													{w.limit.toLocaleString()}
												</span>
											</div>
											<div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100">
												<div
													className={`h-full ${barToneFor(pct, atCap)}`}
													style={{
														width: `${Math.max(2, pct * 100).toFixed(1)}%`,
													}}
													data-testid={`api-rate-limiting-usage-bar-${w.window}`}
												/>
											</div>
										</li>
									);
								})}
							</ul>
						) : null}
					</section>

					<section
						className={CARD}
						aria-label="Burst override form"
						data-testid="api-rate-limiting-burst-form-section"
					>
						<h2 className="text-base font-semibold text-zinc-900">
							Burst override
						</h2>
						<p className="mt-1 text-xs text-zinc-500">
							Leave any field blank to inherit the platform
							default. Values must be integers between 1 and
							10,000,000.
						</p>
						<form
							onSubmit={handleSave}
							className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3"
							data-testid="api-rate-limiting-burst-form"
						>
							<label className="flex flex-col gap-1 text-xs text-zinc-600">
								<span>Requests per minute</span>
								<input
									type="text"
									inputMode="numeric"
									className={INPUT}
									placeholder={`default ${limits?.defaults.rpm ?? ""}`}
									value={rpmInput}
									onChange={(e) =>
										setRpmInput(e.currentTarget.value)
									}
									disabled={saveMut.isPending}
									data-testid="api-rate-limiting-rpm-input"
								/>
							</label>
							<label className="flex flex-col gap-1 text-xs text-zinc-600">
								<span>Requests per hour</span>
								<input
									type="text"
									inputMode="numeric"
									className={INPUT}
									placeholder={`default ${limits?.defaults.rph ?? ""}`}
									value={rphInput}
									onChange={(e) =>
										setRphInput(e.currentTarget.value)
									}
									disabled={saveMut.isPending}
									data-testid="api-rate-limiting-rph-input"
								/>
							</label>
							<label className="flex flex-col gap-1 text-xs text-zinc-600">
								<span>Requests per day</span>
								<input
									type="text"
									inputMode="numeric"
									className={INPUT}
									placeholder={`default ${limits?.defaults.rpd ?? ""}`}
									value={rpdInput}
									onChange={(e) =>
										setRpdInput(e.currentTarget.value)
									}
									disabled={saveMut.isPending}
									data-testid="api-rate-limiting-rpd-input"
								/>
							</label>
							<div className="flex flex-col gap-2 sm:col-span-3 sm:flex-row sm:items-center sm:justify-between">
								<div className="flex flex-wrap gap-2">
									<button
										type="submit"
										className={BTN}
										disabled={saveMut.isPending}
										data-testid="api-rate-limiting-save-submit"
									>
										{saveMut.isPending
											? "Saving."
											: "Save override"}
									</button>
									<button
										type="button"
										className={BTN_GHOST}
										onClick={handleClear}
										disabled={
											clearMut.isPending ||
											!limits?.has_override
										}
										data-testid="api-rate-limiting-clear-submit"
									>
										{clearMut.isPending
											? "Clearing."
											: "Clear to defaults"}
									</button>
								</div>
								{saveMut.isSuccess ? (
									<span
										className="text-xs text-green-700"
										data-testid="api-rate-limiting-save-ok"
									>
										Saved.
									</span>
								) : null}
							</div>
						</form>
						{formError ? (
							<p
								className="mt-2 text-xs text-red-600"
								role="alert"
								data-testid="api-rate-limiting-form-error"
							>
								{formError}
							</p>
						) : null}
						{saveMut.error ? (
							<p
								className="mt-2 text-xs text-red-600"
								role="alert"
							>
								{saveMut.error.message}
							</p>
						) : null}
						{clearMut.error ? (
							<p
								className="mt-2 text-xs text-red-600"
								role="alert"
							>
								{clearMut.error.message}
							</p>
						) : null}
					</section>
				</>
			) : null}
		</main>
	);
}

export default ApiRateLimitingPage;
